import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { didCancel, task } from 'ember-concurrency';
import LazyLoad, { type ILazyLoadInstance } from 'vanilla-lazyload';

import type StoreService from '@ember-data/store';
import type HotelModel from 'admin-panel/models/hotel';
import type ImageModel from 'admin-panel/models/image';
import type SessionInfoService from 'admin-panel/services/session-info';

interface ImageFilterOptions {
  installationIds: string[];
  tags: string[];
}

interface UiImagePickerModalSignature {
  Args: {
    Named: {
      isOpen: boolean;
      onClose: () => void;
      onSelect: (image: ImageModel) => void;
    };
  };
}

const tagFilter = (image: ImageModel, { tags }: ImageFilterOptions) => {
  if (!tags.length) return true;

  return image.tags.some((tag) => tags.includes(tag));
};

const installationFilter = (
  image: ImageModel,
  { installationIds }: ImageFilterOptions
) => {
  if (!installationIds.length) return true;

  return installationIds.includes(image.belongsTo('hotel').id());
};

export default class UiImagePickerModalComponent extends Component<UiImagePickerModalSignature> {
  @service
  declare store: StoreService;

  @service
  declare sessionInfo: SessionInfoService;

  private lazyLoad: ILazyLoadInstance | null = null;
  private imageScrollContainerElement: HTMLElement | null = null;
  private cachedHotelId: string | null = null;

  @tracked
  images: ImageModel[] = [];

  @tracked
  tagFilters: string[] = [];

  @tracked
  installations: { id: string; name: string }[] = [];

  @tracked
  installationFilters: { id: string; name: string }[] = [];

  @tracked
  hasLoadError = false;

  get hotel(): HotelModel | null {
    const { hotel } = this.sessionInfo as SessionInfoService & {
      hotel?: HotelModel;
    };

    return hotel ?? null;
  }

  get isUnavailable() {
    return !this.hotel;
  }

  get hotelId() {
    return this.hotel?.id;
  }

  get selectedInstallationIds() {
    return this.installationFilters.map(({ id }) => id);
  }

  get filteredImages() {
    const installationIds = this.selectedInstallationIds;

    return this.images.filter((image) =>
      [tagFilter, installationFilter].every((filterFn) =>
        filterFn(image, {
          installationIds,
          tags: this.tagFilters,
        })
      )
    );
  }

  get tags() {
    const installationIds = this.selectedInstallationIds;
    const installationTags = this.images
      .filter((image) =>
        installationFilter(image, { installationIds, tags: [] })
      )
      .flatMap(({ tags }) => tags);

    return [...new Set(installationTags)].sort();
  }

  willDestroy() {
    super.willDestroy();

    this.setupModalTask.cancelAll();
    this.teardownLazyLoad();
  }

  setupModalTask = task({ restartable: true }, async () => {
    this.hasLoadError = false;

    const { hotel } = this;
    const hotelId = hotel?.id ?? null;

    if (hotelId !== this.cachedHotelId) {
      this.images = [];
      this.installations = [];
      this.installationFilters = [];
      this.tagFilters = [];
      this.cachedHotelId = hotelId;
    }

    if (!hotel) {
      this.destroyLazyLoad();

      return;
    }

    const hotelMixinIds = hotel.hasMany('hotelMixin').ids();

    let images: ImageModel[] = [];

    try {
      images = (
        await this.store.query('image', {
          'filter[hotel_id_in]': [hotel.id, ...hotelMixinIds],
          'filter[s]': 'id desc',
        })
      ).toArray();
    } catch (error) {
      if (didCancel(error)) return;

      this.images = [];
      this.installations = [];
      this.installationFilters = [];
      this.tagFilters = [];
      this.hasLoadError = true;
      this.destroyLazyLoad();

      return;
    }

    const availableInstallations = hotelMixinIds.map((mixinId) => {
      return {
        id: mixinId,
        name:
          hotel.sharedInstallationMetadata.find((mixin) => mixinId in mixin)?.[
            mixinId
          ] ?? mixinId,
      };
    });

    this.images = images;
    this.installations = [
      { id: hotel.id, name: hotel.name },
      ...availableInstallations.sort((leftInstallation, rightInstallation) =>
        (leftInstallation.name ?? '').localeCompare(
          rightInstallation.name ?? ''
        )
      ),
    ];

    this.initializeLazyLoad();
  });

  @action
  registerImageScrollContainer(element: Element) {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    this.imageScrollContainerElement = element;
    this.initializeLazyLoad();
  }

  @action
  handleOpenStateChange() {
    if (!this.args.isOpen) {
      this.setupModalTask.cancelAll();
      this.teardownLazyLoad();
    }
  }

  @action
  toggleTagFilter(tag: string) {
    if (this.tagFilters.includes(tag)) {
      this.tagFilters = this.tagFilters.filter(
        (filterTag) => filterTag !== tag
      );
    } else {
      this.tagFilters = [...this.tagFilters, tag];
    }

    this.updateLazyLoad();
  }

  @action
  toggleInstallationFilter(installation: { id: string; name: string }) {
    if (
      this.installationFilters.some(
        (filterInstallation) => filterInstallation.id === installation.id
      )
    ) {
      this.installationFilters = this.installationFilters.filter(
        (filterInstallation) => filterInstallation.id !== installation.id
      );
    } else {
      this.installationFilters = [...this.installationFilters, installation];
    }

    this.updateLazyLoad();
  }

  @action
  handleImageClick(image: ImageModel) {
    this.args.onSelect(image);
  }

  initializeLazyLoad() {
    if (
      !this.args.isOpen ||
      this.isUnavailable ||
      !this.imageScrollContainerElement
    ) {
      this.destroyLazyLoad();

      return;
    }

    this.lazyLoad?.destroy();
    this.lazyLoad = new LazyLoad({
      use_native: false,
      cancel_on_exit: true,
      container: this.imageScrollContainerElement,
      elements_selector: '[lazy-load-image]',
    });

    this.updateLazyLoad();
  }

  updateLazyLoad() {
    next(() => {
      this.lazyLoad?.update();
    });
  }

  destroyLazyLoad() {
    this.lazyLoad?.destroy();
    this.lazyLoad = null;
  }

  teardownLazyLoad() {
    this.destroyLazyLoad();
    this.imageScrollContainerElement = null;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::ImagePickerModal': typeof UiImagePickerModalComponent;
    'ui/image-picker-modal': typeof UiImagePickerModalComponent;
  }
}
